import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckCircle, FileText, Search, Upload } from "lucide-react";

function formatDateTime(value) {
  if (!value) return "Never";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusColor(status) {
  switch (status) {
    case "completed":
    case "created":
    case "updated":
      return "green";
    case "processing":
    case "applying":
      return "blue";
    case "failed":
    case "invalid":
      return "red";
    case "duplicate":
    case "review_ready":
      return "orange";
    case "valid":
    case "uploaded":
      return "teal";
    case "skipped":
      return "gray";
    default:
      return "gray";
  }
}

function titleize(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function LeadImports() {
  const [importPayload, setImportPayload] = useState({ imports: [] });
  const [activeImportPayload, setActiveImportPayload] = useState(null);
  const [importsLoading, setImportsLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [applyingImport, setApplyingImport] = useState(false);
  const [activeImportId, setActiveImportId] = useState(null);
  const [duplicatePolicy, setDuplicatePolicy] = useState("skip_duplicates");
  const toast = useToast();

  const loadLeadImports = async () => {
    try {
      setImportsLoading(true);
      const response = await fetch("/api/lead_imports", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load imports");

      setImportPayload(data);
      if (!activeImportId && data.imports?.[0]) setActiveImportId(data.imports[0].id);
    } catch (err) {
      toast({
        title: "Failed to load CSV imports",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImportsLoading(false);
    }
  };

  const loadLeadImport = async (importId) => {
    if (!importId) return;

    try {
      const response = await fetch(`/api/lead_imports/${importId}`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load import");

      setActiveImportPayload(data);
    } catch (err) {
      toast({
        title: "Failed to load CSV import",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadLeadImports();
  }, []);

  useEffect(() => {
    if (!activeImportId) return;

    loadLeadImport(activeImportId);
    if (["completed", "failed"].includes(activeImportPayload?.import?.status)) return;

    const interval = window.setInterval(() => {
      loadLeadImport(activeImportId);
      loadLeadImports();
    }, 2500);

    return () => window.clearInterval(interval);
  }, [activeImportId, activeImportPayload?.import?.status]);

  const uploadCsv = async () => {
    if (!uploadFile) return;

    try {
      setUploadingCsv(true);
      const formData = new FormData();
      formData.append("file", uploadFile);

      const response = await fetch("/api/lead_imports", {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to upload CSV");

      setUploadFile(null);
      setActiveImportId(data.import.id);
      toast({
        title: "CSV upload started",
        description: "Rows are being parsed in the background.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      loadLeadImports();
    } catch (err) {
      toast({
        title: "Failed to upload CSV",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingCsv(false);
    }
  };

  const applyImport = async () => {
    if (!activeImportPayload?.import) return;

    try {
      setApplyingImport(true);
      const response = await fetch(`/api/lead_imports/${activeImportPayload.import.id}/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ duplicate_policy: duplicatePolicy }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to apply import");

      setActiveImportPayload((current) => ({ ...(current || {}), import: data.import }));
      toast({
        title: "CSV import applying",
        description: "Leads are being written to the database.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      loadLeadImports();
    } catch (err) {
      toast({
        title: "Failed to apply CSV import",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setApplyingImport(false);
    }
  };

  const imports = importPayload?.imports || [];
  const activeImport = activeImportPayload?.import || imports.find((leadImport) => leadImport.id === activeImportId);
  const importRows = activeImportPayload?.rows || [];
  const canApplyImport = activeImport?.status === "review_ready";

  return (
    <Box maxW="1500px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="start" spacing={4}>
          <Box>
            <Heading size="lg">CSV Lead Imports</Heading>
            <Text mt={1} color="gray.600" fontSize="sm">
              Upload lead exports, review duplicates, and apply selected rows to the lead database.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button as={RouterLink} to="/leads" variant="outline" leftIcon={<Search size={16} />}>
              Leads
            </Button>
            <Badge colorScheme={activeImport ? statusColor(activeImport.status) : "gray"}>
              {activeImport ? titleize(activeImport.status) : "No Import"}
            </Badge>
          </HStack>
        </HStack>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={4}>
            <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr auto" }} gap={3} alignItems="center">
              <Input
                type="file"
                accept=".csv,.tsv,text/csv,text/tab-separated-values"
                bg="white"
                onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
              />
              <Select
                value={activeImportId || ""}
                onChange={(event) => setActiveImportId(event.target.value ? Number(event.target.value) : null)}
                bg="white"
                isDisabled={importsLoading || imports.length === 0}
              >
                <option value="">Recent imports</option>
                {imports.map((leadImport) => (
                  <option key={leadImport.id} value={leadImport.id}>
                    #{leadImport.id} {leadImport.filename}
                  </option>
                ))}
              </Select>
              <Button
                colorScheme="blue"
                leftIcon={uploadingCsv ? <Spinner size="xs" /> : <Upload size={16} />}
                onClick={uploadCsv}
                isDisabled={!uploadFile || uploadingCsv}
              >
                {uploadingCsv ? "Uploading..." : "Upload CSV"}
              </Button>
            </Grid>

            {activeImport && (
              <>
                <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)", xl: "repeat(8, 1fr)" }} gap={3}>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Total</StatLabel>
                    <StatNumber>{activeImport.total_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>New</StatLabel>
                    <StatNumber>{activeImport.valid_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Duplicates</StatLabel>
                    <StatNumber>{activeImport.duplicate_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Invalid</StatLabel>
                    <StatNumber>{activeImport.invalid_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Created</StatLabel>
                    <StatNumber>{activeImport.created_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Updated</StatLabel>
                    <StatNumber>{activeImport.updated_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Skipped</StatLabel>
                    <StatNumber>{activeImport.skipped_rows_count}</StatNumber>
                  </Stat>
                  <Stat border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                    <StatLabel>Failed</StatLabel>
                    <StatNumber>{activeImport.failed_rows_count}</StatNumber>
                  </Stat>
                </Grid>

                {activeImport.error_message && (
                  <Alert status="error">
                    <AlertIcon />
                    {activeImport.error_message}
                  </Alert>
                )}

                <HStack justify="space-between" align="center" spacing={3} flexWrap="wrap">
                  <HStack spacing={3}>
                    <FileText size={18} />
                    <Box>
                      <Text fontWeight="semibold">{activeImport.filename}</Text>
                      <Text fontSize="xs" color="gray.600">
                        Uploaded {formatDateTime(activeImport.created_at)}
                      </Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3}>
                    <Select
                      value={duplicatePolicy}
                      onChange={(event) => setDuplicatePolicy(event.target.value)}
                      size="sm"
                      bg="white"
                      maxW="220px"
                      isDisabled={!canApplyImport || applyingImport}
                    >
                      <option value="skip_duplicates">Skip duplicates</option>
                      <option value="update_duplicates">Update duplicates</option>
                    </Select>
                    <Button
                      colorScheme="green"
                      size="sm"
                      leftIcon={applyingImport ? <Spinner size="xs" /> : <CheckCircle size={16} />}
                      onClick={applyImport}
                      isDisabled={!canApplyImport || applyingImport}
                    >
                      {applyingImport ? "Applying..." : "Apply Import"}
                    </Button>
                  </HStack>
                </HStack>

                <Box border="1px solid" borderColor="gray.100" borderRadius="md" overflowX="auto">
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Row</Th>
                        <Th>Status</Th>
                        <Th>Business</Th>
                        <Th>Hub ID</Th>
                        <Th>Duplicate</Th>
                        <Th>Issues</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {importRows.map((row) => (
                        <Tr key={row.id}>
                          <Td>{row.row_number}</Td>
                          <Td>
                            <Badge colorScheme={statusColor(row.status)}>{titleize(row.status)}</Badge>
                          </Td>
                          <Td minW="240px">
                            <Text fontWeight="semibold">{row.payload.company_name || "Missing business"}</Text>
                            <Text fontSize="xs" color="gray.600" noOfLines={1}>
                              {row.payload.formatted_address || row.payload.email || row.payload.phone_number || "No contact data"}
                            </Text>
                          </Td>
                          <Td maxW="260px">
                            <Text fontSize="xs" noOfLines={1}>{row.payload.jobber_client_hub_id || "Missing"}</Text>
                          </Td>
                          <Td minW="220px">
                            {row.duplicate_lead ? (
                              <Box>
                                <Text fontSize="sm" fontWeight="semibold">{row.duplicate_lead.company_name}</Text>
                                <Text fontSize="xs" color="gray.600" noOfLines={1}>
                                  {row.duplicate_lead.website_url || row.duplicate_lead.email || "Existing lead"}
                                </Text>
                              </Box>
                            ) : (
                              <Text color="gray.500">None</Text>
                            )}
                          </Td>
                          <Td maxW="260px">
                            {(row.row_errors || []).length > 0 ? (
                              <Text color="red.600" fontSize="xs">{row.row_errors.join(", ")}</Text>
                            ) : (
                              <Text color="gray.500">None</Text>
                            )}
                          </Td>
                        </Tr>
                      ))}
                      {importRows.length === 0 && (
                        <Tr>
                          <Td colSpan={6}>
                            <Text color="gray.600" textAlign="center" py={4}>
                              {activeImport.status === "processing" ? "Parsing rows..." : "No rows to show."}
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
